import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { 
  Search, RefreshCw, Star, X, Trash2, Filter
} from 'lucide-react';

export default function AdminReviews() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({ search: '', rating: '' });

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchReviews();
  }, [user, navigate]);

  // PROMPT BLINDADO: Refetch quando filtro de rating muda
  useEffect(() => {
    if (user && user.user_type === 'admin') {
      fetchReviews(true);
    }
  }, [filters.rating]);

  const fetchReviews = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.rating) params.append('rating', filters.rating);
      
      const res = await axios.get(`${API}/admin/dashboard-v2/reviews?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(res.data.reviews || []);
      setStats(res.data.stats || {});
    } catch (error) {
      console.error(error);
      setReviews([]);
      setStats({ average: 0 });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDelete = async (reviewId, e) => {
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja remover esta avaliação?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/dashboard-v2/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Avaliação removida!');
      fetchReviews(true);
    } catch (error) {
      toast.error('Erro ao remover avaliação');
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const filteredReviews = reviews.filter(review => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matches = 
        review.client_name?.toLowerCase().includes(search) ||
        review.provider_name?.toLowerCase().includes(search) ||
        review.comment?.toLowerCase().includes(search);
      if (!matches) return false;
    }
    return true;
  });

  const clearFilters = () => setFilters({ search: '', rating: '' });
  const hasFilters = filters.search || filters.rating;

  // Calcular distribuição de ratings
  const ratingDistribution = [5, 4, 3, 2, 1].map(rating => ({
    rating,
    count: stats[`rating_${rating}`] || 0,
    percent: reviews.length > 0 
      ? ((stats[`rating_${rating}`] || 0) / reviews.length * 100) 
      : 0
  }));

  if (loading) {
    return (
      <AdminLayout title="Avaliações" subtitle="Carregando...">
        <div className="space-y-6">
          <Skeleton className="h-32" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Avaliações</h1>
            <p className="text-slate-500">Gestão de qualidade e satisfação</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchReviews(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Stats Card */}
        <Card className="p-6">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Média Geral */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-5xl font-bold">{stats.average || 0}</span>
                <Star className="w-10 h-10 text-amber-500 fill-amber-500" />
              </div>
              <p className="text-slate-500">{reviews.length} avaliações</p>
            </div>

            {/* Distribuição */}
            <div className="flex-1 space-y-2">
              {ratingDistribution.map(({ rating, count, percent }) => (
                <div key={rating} className="flex items-center gap-3">
                  <div className="flex items-center gap-1 w-12">
                    <span className="text-sm font-medium">{rating}</span>
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  </div>
                  <Progress value={percent} className="flex-1 h-2" />
                  <span className="text-sm text-slate-500 w-12 text-right">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por cliente, prestador ou comentário..."
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              {[5, 4, 3, 2, 1].map(rating => (
                <Button
                  key={rating}
                  variant={filters.rating === String(rating) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilters({ 
                    ...filters, 
                    rating: filters.rating === String(rating) ? '' : String(rating) 
                  })}
                >
                  {rating} <Star className="w-3 h-3 ml-1 fill-current" />
                </Button>
              ))}
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> Limpar
              </Button>
            )}
          </div>
        </Card>

        {/* Reviews List */}
        <div className="space-y-3">
          {filteredReviews.length > 0 ? (
            filteredReviews.map((review) => (
              <Card key={review.id} className="p-4">
                <div className="flex items-start gap-4">
                  <Avatar>
                    <AvatarFallback className="bg-blue-100 text-blue-700">
                      {getInitials(review.client_name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold">{review.client_name || 'Cliente'}</p>
                      <span className="text-slate-400">→</span>
                      <p className="text-slate-600">{review.provider_name || 'Prestador'}</p>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-4 h-4 ${i < review.rating ? 'text-amber-500 fill-amber-500' : 'text-gray-200'}`}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-slate-400">{formatDate(review.created_at)}</span>
                    </div>
                    
                    {review.comment && (
                      <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                        "{review.comment}"
                      </p>
                    )}
                  </div>

                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-600"
                    onClick={(e) => handleDelete(review.id, e)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-12 text-center">
              <Star className="w-16 h-16 mx-auto mb-4 text-slate-200" />
              <h3 className="text-lg font-semibold mb-2">Nenhuma avaliação encontrada</h3>
              <p className="text-slate-500">
                {hasFilters 
                  ? 'Tente ajustar os filtros'
                  : 'As avaliações dos clientes aparecerão aqui'}
              </p>
            </Card>
          )}
        </div>

        {/* Summary */}
        {filteredReviews.length > 0 && (
          <p className="text-sm text-slate-500 text-center">
            Mostrando {filteredReviews.length} de {reviews.length} avaliações
          </p>
        )}
      </div>
    </AdminLayout>
  );
}
